import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { scheduleAPI, lookupAPI } from '../services/api.ts';
import { usePopup } from '../context/PopupContext.tsx';
import { useConfirm } from '../context/ConfirmContext.tsx';
import MobileDatePicker from '../components/MobileDatePicker.tsx';
import MobileDateTimePicker from '../components/MobileDateTimePicker.tsx';
import { toast } from 'react-toastify';
import PesticideInputForm from '../components/PesticideInputForm.tsx';
import { format } from 'date-fns';
import MapPicker from '../components/MapPicker.tsx';
import { useAuth } from '../context/AuthContext.tsx';

// Update interface to handle image objects with IDs
interface ImageItem {
  id: number;
  imageUrl: string;
  typeId: number;
  createdBy?: string;
  createdDateTime?: string;
  pestControlScheduleId?: number;
  pestControlSchedule?: any;
}

// Update interface to handle more detailed image objects with IDs
interface ScheduleItem {
  id: number;
  projectName: string;
  address: string;
  customerName?: string; // Add customerName field
  time: string;
  franchiseeId: number;
  date: string;
  startTime: string | null;
  managerStartedDate?: string | null;
  managerCompletedDate?: string | null;
  pic: string;
  franchiseeBranchNo: string | null;
  rating: number;
  signature: string | null;
  remarks: string | null;
  isCompleted: boolean;
  workFlowStatusId?: number; // 0=Pending, 1=Progress, 2=Completed
  beforeImages?: (string | ImageItem)[];
  afterImages?: (string | ImageItem)[];
  contactNo?: string; // Add contact number field
  lastSignatureRequestTime?: string | null; // Add property for tracking when signature was last requested
  // Add new properties for completed schedules
  pestControlScheduleRecommendations?: Array<{
    id: number;
    pestControlScheduleId: number;
    recommendationId: number;
    createdBy: string;
    createdDateTime: string;
    updatedBy: string | null;
    updatedDateTime: string | null;
    addtionalInfromation: string;
    pestControlSchedule: any | null;
  }>;
  pestControlScheduleInfestation?: Array<{
    id: number;
    pestControlScheduleId: number;
    createdDateTime: string;
    updatedDateTime: string | null;
    createdBy: string;
    updatedBy: string | null;
    area: string;
    infestationLevel: string;
    infestationId: number;
    pestControlSchedule: any | null;
  }>;
  pestControlSchedulePestType?: Array<{
    id: number;
    pestControlScheduleId: number;
    pestTypeId: number;
    createdDateTime: string;
    updatedDateTime: string | null;
    createdBy: string;
    updatedBy: string | null;
    otherInformation: string;
    pestControlSchedule: any | null;
  }>;
  completionData?: {
    followUpRequired: boolean;
    followUpDate?: string;
    maintainCleanliness: boolean;
    sealCracks: boolean;
    otherRecommendation?: string;
    otherRemarks?: string;
    infestationLevel?: 'none' | 'low' | 'medium' | 'high';
    affectedAreas?: string;
    postTreatmentActivity?: 'reduced' | 'no_change' | 'increased';
    treatedPests?: string[];
    pestInfestationLevels?: {
      rats: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
      ants: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
      cockroach: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
      others: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
      subterraneanTermites?: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
    };
    OtherPestSpecification?: string;
  };
}

// ZoomableImage component for handling image zooming
interface ZoomableImageProps {
  src: string | ImageItem;
  alt: string;
  index: number;
  onDelete?: (index: number) => void;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt, index, onDelete }) => {
  const [zoomed, setZoomed] = useState(false);
  const [imageError, setImageError] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Toggle zoom state
  const toggleZoom = () => {
    setZoomed(!zoomed);
  };

  // Handle key press for window events (Escape key)
  const handleGlobalKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
      setZoomed(false);
    }
  };
  
  // Handle key press for React elements
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setZoomed(false);
    }
  };

  // Add/remove event listener for Escape key
  useEffect(() => {
    if (zoomed) {
      window.addEventListener('keydown', handleGlobalKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [zoomed]);

  // Extract the image URL from either a string or an ImageItem object
  const getImageUrl = () => {
    try {
      // Log the image source for debugging
      console.log(`Getting image URL for index ${index}:`, src);
      
      if (typeof src === 'string') {
        // For blob URLs or complete URLs, return as is
        if (src.startsWith('blob:') || src.startsWith('http')) {
          return src;
        }
        // For relative paths, ensure they have the correct base URL
        return src;
      } else if (src && typeof src === 'object') {
        // Check if the object has imageUrl property
        if ('imageUrl' in src && src.imageUrl) {
          const imageUrl = src.imageUrl;
          console.log(`Object image URL for index ${index}:`, imageUrl);
          
          // If imageUrl is already a full URL, return it
          if (imageUrl.startsWith('http')) {
            return imageUrl;
          }
          
          // Otherwise return as is (assume it's a correct path)
          return imageUrl;
        } else {
          console.error(`Image object missing imageUrl property at index ${index}:`, src);
          return '';
        }
      }
    } catch (error) {
      console.error(`Error processing image URL at index ${index}:`, error);
    }
    
    // If all else fails, return empty string
    return '';
  };

  // Handle image load error
  const handleImageError = () => {
    const url = getImageUrl();
    console.error(`Failed to load image at index ${index}: ${url}`);
    console.error(`Full image object:`, src);
    
    // Try to diagnose common URL issues
    if (!url) {
      console.error(`Empty URL for image at index ${index}`);
    } else if (url.includes('undefined')) {
      console.error(`URL contains 'undefined': ${url}`);
    } else if (url.includes('null')) {
      console.error(`URL contains 'null': ${url}`);
    }
    
    setImageError(true);
  };

  return (
    <div className="relative group">
      {imageError ? (
        // Show error placeholder when image fails to load
        <div className="w-full h-32 bg-gray-200 rounded flex items-center justify-center">
          <div className="text-red-500 text-center p-2">
            <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span className="block text-xs mt-1">Image failed to load</span>
        </div>
      </div>
      ) : (
        <img 
          src={getImageUrl()} 
              alt={alt} 
          className="w-full h-32 object-cover rounded cursor-pointer" 
          onClick={toggleZoom}
          onError={handleImageError}
            />
      )}
      
      {onDelete && (
            <button 
          onClick={(e) => {
            e.stopPropagation();
            onDelete(index);
          }}
          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none transition-opacity opacity-80 hover:opacity-100"
          title="Delete image"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
      )}
      
      {zoomed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
          ref={modalRef} 
          onClick={toggleZoom} 
          tabIndex={0} 
          onKeyDown={handleKeyDown}
        >
          <div className="max-w-4xl max-h-full p-4">
            <img src={getImageUrl()} alt={alt} className="max-w-full max-h-90vh object-contain" onError={handleImageError} />
          </div>
        </div>
      )}
    </div>
  );
};

// Interface for recommendations
interface Recommendation {
  id: number;
  name: string;
  requiresDate?: boolean;
  requiresTextField?: boolean;
}

// Define mapInfestationLevel and mapPostTreatmentActivity functions
const mapInfestationLevel = (infestationLevelId: number): 'none' | 'low' | 'medium' | 'high' => {
  switch (infestationLevelId) {
    case 1:
      return 'low';
    case 2:
      return 'medium';
    case 3:
      return 'high';
    default:
      return 'none';
  }
};

const mapPostTreatmentActivity = (postTreatmentActivityId: number): 'reduced' | 'no_change' | 'increased' => {
  switch (postTreatmentActivityId) {
    case 1:
      return 'reduced';
    case 2:
      return 'increased';
    default:
      return 'no_change';
  }
};

// Update type definition to include OtherPestSpecification
interface CompletionData {
  followUpRequired: boolean;
  followUpDate?: string;
  maintainCleanliness: boolean;
  sealCracks: boolean;
  otherRecommendation?: string;
  otherRemarks?: string;
  infestationLevel?: 'none' | 'low' | 'medium' | 'high';
  affectedAreas?: string;
  postTreatmentActivity?: 'reduced' | 'no_change' | 'increased';
  treatedPests?: string[];
  pestInfestationLevels?: {
    rats: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
    ants: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
    cockroach: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
    others: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
    subterraneanTermites?: { level: 'none' | 'low' | 'medium' | 'high'; area: string };
  };
  OtherPestSpecification?: string;
}

// Define setSignaturePath and setCompletionValues functions
const setSignaturePath = (path: string) => {
  // Implementation for setting signature path
};

const setCompletionValues = (values: any) => {
  // Implementation for setting completion values
};

// Update type definition to include subterraneanTermites
interface PestInfestationLevel {
  Id: number;
  Level: 'none' | 'low' | 'medium' | 'high';
  Area: string;
  AdditionalInformation?: string;
}

type PestInfestationLevels = PestInfestationLevel[];

// Update the type definition for the API call to include OtherPestSpecification
interface MarkAsCompletedData {
  OtherRemarks: string;
  InfestationLevel: number;
  PostTreatmentActivity: number;
  SiteManagerCompletedBy: string;
  AffectedArea: string;
  Recommendation: Array<{
    Id: number;
    AdditionalInformation: string;
  }>;
  TreatedPests: Array<{
    Id: number;
    AdditionalInformation: string;
  }>;
  PestInfestationLevels: PestInfestationLevel[];
  OtherPestSpecification?: string;
  IsRequiredServiceReport?: boolean; // Add service report field
  ContactNo?: string; // Add contact number field
}

// Ensure compatibility in state updates
const updateScheduleState = (prev: ScheduleItem | null): ScheduleItem | null => {
  if (!prev) return null;
  return {
    ...prev,
    isCompleted: true,
    workFlowStatusId: 2,
    completionData: {
      ...prev.completionData,
      followUpRequired: false,
      followUpDate: '',
      maintainCleanliness: false,
      sealCracks: false,
      otherRecommendation: '',
      otherRemarks: '',
      infestationLevel: 'none',
      affectedAreas: '',
      postTreatmentActivity: 'no_change',
      treatedPests: [],
      pestInfestationLevels: {
        rats: { level: 'none', area: '' },
        ants: { level: 'none', area: '' },
        cockroach: { level: 'none', area: '' },
        others: { level: 'none', area: '' },
        subterraneanTermites: { level: 'none', area: '' },
      },
      OtherPestSpecification: '',
    },
  };
};

// Format phone number for WhatsApp
const formatWhatsAppNumber = (phone: string): string => {
  // Remove all non-numeric characters
  const numericOnly = phone.replace(/[^0-9]/g, '');
  
  // Check if the number already starts with '6' (Malaysia country code)
  if (numericOnly.startsWith('6')) {
    return numericOnly;
  } else {
    // Add '6' as country code
    return `6${numericOnly}`;
  }
};

const ScheduleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = usePopup();
  const { confirm } = useConfirm();
  const { user } = useAuth();
  
  // Add a flag to prevent unnecessary GetSchedule calls
  const isUpdatingImages = useRef(false);
  
  const [schedule, setSchedule] = useState<ScheduleItem | null>(null);
  // Create separate state for before and after images to prevent re-rendering issues
  const [beforeImages, setBeforeImages] = useState<(string | ImageItem)[]>([]);
  const [afterImages, setAfterImages] = useState<(string | ImageItem)[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'before' | 'after' | 'pesticides'>('details');
  const [notes, setNotes] = useState<string>('');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [activeIndicator, setActiveIndicator] = useState<'details' | 'before' | 'after' | null>(null);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null); // Add touch start time tracking
  
  // Add state for recommendations
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [pestTypes, setPestTypes] = useState<{ id: number; name: string }[]>([]);
  const [infestationTypes, setInfestationTypes] = useState<{ id: number; name: string }[]>([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState<number[]>([]);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [otherFieldText, setOtherFieldText] = useState('');
  
  // Add state for completion form
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [maintainCleanliness, setMaintainCleanliness] = useState(false);
  const [sealCracks, setSealCracks] = useState(false);
  const [otherRecommendation, setOtherRecommendation] = useState('');
  const [otherRemarks, setOtherRemarks] = useState('');
  const [infestationLevel, setInfestationLevel] = useState<'low' | 'medium' | 'high' | ''>('');
  const [affectedAreas, setAffectedAreas] = useState('');
  const [postTreatmentActivity, setPostTreatmentActivity] = useState<'reduced' | 'no_change' | 'increased' | ''>('');
  
  // Add new state variables for pest types and infestation levels
  const [treatedPests, setTreatedPests] = useState<number[]>([]);
  const [pestInfestationLevels, setPestInfestationLevels] = useState<PestInfestationLevels>([]);
  
  // Add new state variable for other pest specification
  const [otherPestSpecification, setOtherPestSpecification] = useState('');
  
  // Add state for service report and contact confirmation
  const [showServiceReportConfirm, setShowServiceReportConfirm] = useState(false);
  const [showContactConfirm, setShowContactConfirm] = useState(false);
  const [isRequiredServiceReport, setIsRequiredServiceReport] = useState(false);
  const [contactNo, setContactNo] = useState("");
  const [isPrintingReport, setIsPrintingReport] = useState(false);
  const [isReminding, setIsReminding] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  
  // Editing state for manager working times
  const [isEditingManagerTimes, setIsEditingManagerTimes] = useState(false);
  const [managerStartInput, setManagerStartInput] = useState('');
  const [managerEndInput, setManagerEndInput] = useState('');
  const [savingManagerTimes, setSavingManagerTimes] = useState(false);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [activeManagerField, setActiveManagerField] = useState<'start' | 'end' | null>(null);

  const parseLocalInput = (val: string): Date | null => {
    if (!val) return null;
    try { return new Date(val); } catch { return null; }
  };

  const openPickerFor = (field: 'start' | 'end') => {
    setActiveManagerField(field);
    setShowDateTimePicker(true);
  };

  const handleSaveManagerTimes = async () => {
    if (!schedule) return;
    // Validate order: start <= end if both provided
    if (managerStartInput && managerEndInput) {
      const start = new Date(managerStartInput).getTime();
      const end = new Date(managerEndInput).getTime();
      if (start > end) {
        showError('Start time cannot be later than end time');
        return;
      }
    }
    setSavingManagerTimes(true);
    try {
      await scheduleAPI.updateManagerWorkingDateTime(schedule.id, {
        StartWorkingTime: managerStartInput ? managerStartInput : null,
        EndWorkingTime: managerEndInput ? managerEndInput : null
      });
      setSchedule(prev => prev ? {
        ...prev,
        managerStartedDate: managerStartInput || null,
        managerCompletedDate: managerEndInput || null
      } : prev);
      showSuccess('Manager working times updated');
      setIsEditingManagerTimes(false);
    } catch (error) {
      console.error('Error updating manager working times:', error);
      showError('Failed to update manager working date/time');
    } finally {
      setSavingManagerTimes(false);
    }
  };

  const handleConfirmDateTime = (date: Date | null) => {
    setShowDateTimePicker(false);
    if (!activeManagerField) return;
    const value = date ? format(date, "yyyy-MM-dd'T'HH:mm") : '';
    if (activeManagerField === 'start') {
      if (managerEndInput && value) {
        const picked = new Date(value).getTime();
        const end = new Date(managerEndInput).getTime();
        if (picked > end) {
          showError('Start time cannot be later than end time');
          setActiveManagerField(null);
          return;
        }
      }
      setManagerStartInput(value);
    }
    if (activeManagerField === 'end') {
      if (managerStartInput && value) {
        const picked = new Date(value).getTime();
        const start = new Date(managerStartInput).getTime();
        if (picked < start) {
          showError('End time cannot be earlier than start time');
          setActiveManagerField(null);
          return;
        }
      }
      setManagerEndInput(value);
    }
    setActiveManagerField(null);
  };

  const initManagerInputsFromSchedule = () => {
    if (!schedule) return;
    const toLocalInput = (dt?: string | null) => {
      if (!dt) return '';
      try {
        return format(new Date(dt), "yyyy-MM-dd'T'HH:mm");
      } catch {
        return '';
      }
    };
    setManagerStartInput(toLocalInput(schedule.managerStartedDate));
    setManagerEndInput(toLocalInput(schedule.managerCompletedDate));
  };

  useEffect(() => {
    initManagerInputsFromSchedule();
  }, [schedule]);
  
  // Debug useEffect for infestationTypes
  useEffect(() => {
    console.log('infestationTypes updated:', infestationTypes);
  }, [infestationTypes]);
  
  // Debug useEffect for recommendations and schedule data
  useEffect(() => {
    if (schedule && schedule.workFlowStatusId === 2) {
      console.log('Completed Schedule Data:', schedule);
      console.log('pestControlScheduleRecommendations:', schedule.pestControlScheduleRecommendations);
      console.log('recommendations array:', recommendations);
      console.log('pestTypes array:', pestTypes);
      
      // Debug pest type mapping
      if (schedule.pestControlSchedulePestType && schedule.pestControlSchedulePestType.length > 0) {
        schedule.pestControlSchedulePestType.forEach(pest => {
          const pestType = pestTypes.find(type => type.id === pest.pestTypeId);
          console.log(`Pest Type ID ${pest.pestTypeId} maps to: ${pestType ? pestType.name : 'Not Found'}`);
        });
      }
    }
  }, [schedule, recommendations, pestTypes]);
  
  // Minimum swipe distance (in px)
  const minSwipeDistance = 150; // Increased from 50 to 100 for less sensitivity
  
  // At the top of the file, add state for the date picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Add a function to format dates
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Add a function to handle date selection
  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      setFollowUpDate(date.toISOString().split('T')[0]); // Format as YYYY-MM-DD for the backend
    } else {
      setFollowUpDate(''); // Clear the followUpDate when date is null
    }
    setShowDatePicker(false);
  };

  // Add a function to handle "today" button
  const handleSelectToday = () => {
    const today = new Date();
    handleDateSelect(today);
  };

  // Add a function to clear date selection
  const handleClearDate = () => {
    setSelectedDate(null);
    setFollowUpDate('');
    setShowDatePicker(false);
  };

  // Add a full-screen calendar picker
  const FullScreenCalendar = () => (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <div className="flex justify-between items-center p-4 bg-primary text-white">
        <button onClick={() => setShowDatePicker(false)} className="text-white">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-lg font-bold">Select Date</h2>
        <button onClick={handleSelectToday} className="text-white">
          Today
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {/* Calendar grid here */}
        <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium text-gray-600">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div className="grid grid-cols-7 gap-1 mt-1">
          {[...Array(35)].map((_, index) => {
            const day = index % 31 + 1;
            const isToday = day === new Date().getDate() && new Date().getMonth() === new Date().getMonth();
            const isSelected = selectedDate && day === selectedDate.getDate() && selectedDate.getMonth() === new Date().getMonth();
            return (
              <button 
                key={index} 
                className={`w-10 h-10 flex items-center justify-center text-sm rounded-full ${
                  isSelected 
                    ? 'bg-blue-500 text-white' 
                    : isToday 
                      ? 'border border-blue-500 text-blue-500' 
                    : 'hover:bg-gray-100'
                }`}
                onClick={() => {
                  const newDate = new Date();
                  newDate.setDate(day);
                  handleDateSelect(newDate);
                }}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-4 border-t flex justify-between">
        <button onClick={handleClearDate} className="text-primary font-medium">
          Clear
        </button>
        <button onClick={() => setShowDatePicker(false)} className="text-primary font-medium">
          Done
        </button>
      </div>
    </div>
  );

  // Render the full-screen calendar picker
  {showDatePicker && <FullScreenCalendar />}

  // Modify the fetchSchedule function to respect the isUpdatingImages flag
  useEffect(() => {
    const fetchSchedule = async () => {
      // Don't fetch schedule if we're in the middle of updating images
      if (isUpdatingImages.current) {
        console.log('Skipping schedule fetch because images are being updated');
        return;
      }
      
      try {
        console.log('Fetching schedule details...');
        const response = await scheduleAPI.getScheduleDetails(Number(id) || 0);
        if (response && response.data) {
          // Extract image data to separate state variables
          const scheduleData = response.data;
          //setBeforeImages(scheduleData.beforeImages || []);
          //setAfterImages(scheduleData.afterImages || []);
          
          // Process completion data if the schedule is completed
          let processedScheduleData = { ...scheduleData };
          
          // Check if schedule is completed either by isCompleted flag or workFlowStatusId
          const isCompletedSchedule = scheduleData.isCompleted || scheduleData.workFlowStatusId === 2;
          
          // Debug log for pestControlScheduleRecommendations
          console.log('Schedule Data:', scheduleData);
          console.log('pestControlScheduleRecommendations:', scheduleData.pestControlScheduleRecommendations);
          console.log('pestControlSchedulePestType:', scheduleData.pestControlSchedulePestType);
          console.log('pestTypes array:', pestTypes);
          
          // Debug pest type mapping
          if (scheduleData.pestControlSchedulePestType && scheduleData.pestControlSchedulePestType.length > 0) {
            scheduleData.pestControlSchedulePestType.forEach(pest => {
              const pestType = pestTypes.find(type => type.id === pest.pestTypeId);
              console.log(`Pest Type ID ${pest.pestTypeId} maps to: ${pestType ? pestType.name : 'Not Found'}`);
            });
          }
          
          if (isCompletedSchedule) {
            // Map backend completion data to frontend structure
            const completionData = {
              followUpRequired: false,
              followUpDate: '',
              maintainCleanliness: false,
              sealCracks: false,
              otherRecommendation: '',
              otherRemarks: scheduleData.otherRemarks || '',
              infestationLevel: mapInfestationLevel(scheduleData.infestationLevelId),
              affectedAreas: scheduleData.affectedArea || '',
              postTreatmentActivity: mapPostTreatmentActivity(scheduleData.postTreatmentActivityId),
              
              // Map pestControlSchedulePestType to treatedPests
              treatedPests: Array.isArray(scheduleData.pestControlSchedulePestType) ? 
                scheduleData.pestControlSchedulePestType.map(pest => pest.pestTypeId) : [],
              
              // Map pestControlScheduleInfestation to pestInfestationLevels
              pestInfestationLevels: Array.isArray(scheduleData.pestControlScheduleInfestation) ?
                scheduleData.pestControlScheduleInfestation.reduce((acc, infestation) => {
                  // Find the corresponding infestation type from infestationTypes
                  const infestationType = infestationTypes.find(type => type.id === infestation.infestationId);
                  const pestTypeName = infestationType ? infestationType.name.toLowerCase() : 'others';
                  
                  return {
                    ...acc,
                    [pestTypeName]: { 
                      level: infestation.infestationLevel || 'none', 
                      area: infestation.area || '' 
                    }
                  };
                }, {
                  rats: { level: 'none', area: '' },
                  ants: { level: 'none', area: '' },
                  cockroach: { level: 'none', area: '' },
                  others: { level: 'none', area: '' },
                  subterraneanTermites: { level: 'none', area: '' }
                }) : {
                  rats: { level: 'none', area: '' },
                  ants: { level: 'none', area: '' },
                  cockroach: { level: 'none', area: '' },
                  others: { level: 'none', area: '' },
                  subterraneanTermites: { level: 'none', area: '' }
                },
              
              // Set OtherPestSpecification from pestControlSchedulePestType
              OtherPestSpecification: ''
            };
            
            // Find "Others" pest type and set its otherInformation if available
            if (Array.isArray(scheduleData.pestControlSchedulePestType)) {
              // Find the ID for "Others" pest type
              const othersPestType = pestTypes.find(type => type.name === 'Others');
              if (othersPestType) {
                const othersPest = scheduleData.pestControlSchedulePestType.find(
                  pest => pest.pestTypeId === othersPestType.id
                );
                if (othersPest && othersPest.otherInformation) {
                  completionData.OtherPestSpecification = othersPest.otherInformation;
                }
              }
            }
            
            processedScheduleData.completionData = completionData;
            
            // Also set recommendations to state if needed
            if (Array.isArray(scheduleData.pestControlScheduleRecommendations)) {
              const recommendationIds = scheduleData.pestControlScheduleRecommendations.map(
                rec => rec.recommendationId
              );
              setSelectedRecommendations(recommendationIds);
            }
            
            // Set pest infestation levels for the state
            if (Array.isArray(scheduleData.pestControlScheduleInfestation)) {
              const formattedPestInfestationLevels = scheduleData.pestControlScheduleInfestation.map(infestation => ({
                Id: infestation.infestationId,
                Level: infestation.infestationLevel || 'none',
                Area: infestation.area || ''
              }));
              setPestInfestationLevels(formattedPestInfestationLevels);
            }
            
            // Set treated pests for the state
            if (Array.isArray(scheduleData.pestControlSchedulePestType)) {
              const pestTypeIds = scheduleData.pestControlSchedulePestType.map(pest => pest.pestTypeId);
              setTreatedPests(pestTypeIds);
            }
          }
          
          // Normalize remarks and address fields (handle both camelCase and PascalCase)
          const normalizedSchedule = {
            ...processedScheduleData,
            remarks: processedScheduleData.remarks || processedScheduleData.Remarks || null,
            address: processedScheduleData.address || processedScheduleData.Address || null
          };
          
          // Set the schedule data to state
          setSchedule(normalizedSchedule);
          
          // If the schedule has remarks, set them to the notes state variable
          if (processedScheduleData.notes) {
            setNotes(processedScheduleData.notes);
          }

          // If completionData exists, set the relevant state values
          if (processedScheduleData.completionData) {
            const { completionData } = processedScheduleData;
            
            // Set other remarks
            if (completionData.otherRemarks) {
              setOtherRemarks(completionData.otherRemarks);
            }
            
            // Set infestation level
            if (completionData.infestationLevel) {
              setInfestationLevel(completionData.infestationLevel);
            }
            
            // Set affected areas
            if (completionData.affectedAreas) {
              setAffectedAreas(completionData.affectedAreas);
            }
            
            // Set post treatment activity
            if (completionData.postTreatmentActivity) {
              setPostTreatmentActivity(completionData.postTreatmentActivity);
            }
            
            // Set treated pests - these are now numeric IDs
            if (completionData.treatedPests && Array.isArray(completionData.treatedPests)) {
              setTreatedPests(completionData.treatedPests);
            }
            
            // Set pest infestation levels
            if (completionData.pestInfestationLevels) {
              setPestInfestationLevels(prev => ({
                ...prev,
                ...completionData.pestInfestationLevels
              }));
            }
            
            // Set other pest specification if available
            if (completionData.OtherPestSpecification) {
              setOtherPestSpecification(completionData.OtherPestSpecification);
            }
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching schedule details:', error);
        showError('Failed to load schedule details. Please try again.');
        navigate('/schedule');
      } finally {
        setLoading(false);
      }
    };
    
    // Also fetch pest types here
    const fetchPestTypes = async () => {
      try {
        console.log('[ScheduleDetail] 🕒 Fetching pest types...');
        const data = await lookupAPI.getPestTypes();
        if (data && data.pestType && Array.isArray(data.pestType)) {
          console.log('[ScheduleDetail] ✅ Pest types loaded:', data.pestType);
          setPestTypes(data.pestType);
        } else {
          console.error('[ScheduleDetail] ❌ Invalid pest types data format:', data);
        }
      } catch (error) {
        console.error('[ScheduleDetail] ❌ Failed to fetch pest types:', error);
      }
    };
    
    fetchSchedule();
    fetchPestTypes(); // Add pest types fetch here
  }, [id, navigate, showError]);

  // Fetch recommendations and pest types
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setRecommendationLoading(true);
        let data = await lookupAPI.getRecommendations();
        
        // Add metadata to recommendations based on ID
        // This is a temporary solution until the backend provides this information
        data = data.map((rec: any) => {
          const metadata: Partial<Recommendation> = {};
          
          // Determine if this recommendation needs a date field
          if (rec.id === 1) { // Follow up required
            metadata.requiresDate = true;
          }
          // Determine if this recommendation needs a text field
          else if (rec.id === 6) { // Other recommendations
            metadata.requiresTextField = true;
          }
          
          return { ...rec, ...metadata };
        });
        
        setRecommendations(data);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      } finally {
        setRecommendationLoading(false);
      }
    };
    
    // Fetch infestation types
    const fetchInfestationTypes = async () => {
      try {
        console.log('[ScheduleDetail] 🕒 Fetching infestation types...');
        const data = await lookupAPI.getInfestationTypes();
        
        console.log('[ScheduleDetail] Raw infestation types data:', JSON.stringify(data));
        
        if (!data) {
          console.error('[ScheduleDetail] ❌ No data returned from getInfestationTypes');
          return;
        }
        
        if (Array.isArray(data)) {
          if (data.length === 0) {
            console.warn('[ScheduleDetail] ⚠️ Empty array returned from getInfestationTypes');
          } else {
            console.log('[ScheduleDetail] ✅ Successfully loaded', data.length, 'infestation types:', data);
          }
          
          // Force a re-render by creating a new array
          setInfestationTypes([...data]);
          
          // Log the state update immediately
          console.log('[ScheduleDetail] infestationTypes state is being updated with:', [...data]);
        } else {
          console.error('[ScheduleDetail] ❌ Invalid data format from getInfestationTypes. Expected array, got:', typeof data);
          
          // If data is an object with an array property, try to extract it
          if (typeof data === 'object' && data !== null) {
            console.log('[ScheduleDetail] Attempting to extract array from object. Object keys:', Object.keys(data));
            
            // Check if there's any property that's an array and might contain the infestation types
            const possibleArrayProps = Object.entries(data).filter(([key, value]) => Array.isArray(value));
            
            if (possibleArrayProps.length > 0) {
              console.log('[ScheduleDetail] Found array properties:', possibleArrayProps.map(([key]) => key));
              
              // Use the first array property found
              const [key, arrayData] = possibleArrayProps[0];
              console.log(`[ScheduleDetail] Using '${key}' property with ${(arrayData as any[]).length} items`);
              
              setInfestationTypes([...(arrayData as any[])]);
            }
          }
        }
      } catch (error) {
        console.error('[ScheduleDetail] ❌ Failed to fetch infestation types:', error);
      }
    };

    fetchRecommendations();
    fetchInfestationTypes();
    // We don't need to call fetchPestTypes here as it's already called in the first useEffect
  }, []);

  // Handle touch start
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // reset touch end
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartTime(Date.now()); // Record the time when touch starts
  };

  // Handle touch move
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    
    // Add live feedback while swiping - increased threshold from 20 to 50
    if (touchStart && Math.abs(touchStart - e.targetTouches[0].clientX) > 50) {
      const direction = touchStart > e.targetTouches[0].clientX ? 'left' : 'right';
      
      // Determine which indicator to highlight based on current tab and swipe direction
      if (direction === 'left') {
        if (activeTab === 'details') setActiveIndicator('before');
        else if (activeTab === 'before') setActiveIndicator('after');
      } else if (direction === 'right') {
        if (activeTab === 'after') setActiveIndicator('before');
        else if (activeTab === 'before') setActiveIndicator('details');
      }
    } else {
      setActiveIndicator(null);
    }
  };

  // Handle touch end - determine swipe direction
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartTime) return;
    
    const touchDuration = Date.now() - touchStartTime;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Check if swipe duration is reasonable (not too quick accidental swipe) - at least 50ms
    if ((isLeftSwipe || isRightSwipe) && touchDuration > 50) {
      // Tabs order: details -> before -> after -> pesticides
      if (isLeftSwipe) {
        // Swipe left: move to next tab
        setSwipeDirection('left');
        if (activeTab === 'details') setActiveTab('before');
        else if (activeTab === 'before') setActiveTab('after');
        else if (activeTab === 'after') setActiveTab('pesticides');
      } else if (isRightSwipe) {
        // Swipe right: move to previous tab
        setSwipeDirection('right');
        if (activeTab === 'pesticides') setActiveTab('after');
        else if (activeTab === 'after') setActiveTab('before');
        else if (activeTab === 'before') setActiveTab('details');
      }
    }
    
    // Reset touch coordinates and active indicator
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartTime(null);
    setActiveIndicator(null);
  };

  // Update the fetchImages function to set isUpdatingImages flag
  const fetchImages = async (typeId: number) => {
    try {
      if (schedule) {
        // Set flags to prevent GetSchedule calls
        isUpdatingImages.current = true;
        setIsLoadingImages(true);
        

        const response = await scheduleAPI.getImages(schedule.id, typeId);
        const processedImages = processApiImagesResponse(response);
        
        if (processedImages.length > 0) {
          // Update the correct image state based on type
          if (typeId === 1) {
            setBeforeImages(processedImages);
          } else if (typeId === 2) {
            setAfterImages(processedImages);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      isUpdatingImages.current = false;
      setIsLoadingImages(false);
    
    }
  };

  // Update the tab change handler with extra protection against unwanted fetches
  const handleTabChange = (tab: 'details' | 'before' | 'after' | 'pesticides') => {
    // Don't change tabs during image operations
    if (isUpdatingImages.current) {
      console.log('Skipping tab change because images are being updated');
      return;
    }
    if(tab === 'before'){
      fetchImages(1);
    }
    if(tab === 'after'){
      fetchImages(2);
    }
    setActiveTab(tab);
    // Reset swipe direction when changing tabs deliberately
    setSwipeDirection(null);
  };

  // Return loading UI if still loading
  if (loading) {
    return (
      <div className="container-app py-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="container-app py-6">
        <div className="flex flex-col items-center justify-center">
          <div className="bg-white rounded-lg shadow p-6 text-center w-full max-w-md">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2 text-gray-800">Schedule Not Found</h2>
            <p className="text-gray-600 mb-6">
            The schedule you are looking for does not exist or has been removed.
          </p>
          <button 
            onClick={() => navigate(-1)}
              className="btn btn-primary inline-block w-full py-3"
          >
            Go Back
          </button>
          </div>
        </div>
      </div>
    );
  }

  // Get animation class based on swipe direction
  const getAnimationClass = () => {
    if (!swipeDirection) return '';
    return swipeDirection === 'left' 
      ? 'animate-slide-left' 
      : 'animate-slide-right';
  };

  // Add a loading overlay component
  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg flex items-center">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mr-3"></div>
        <p className="text-gray-700">Processing...</p>
      </div>
    </div>
  );

  // Process image responses
  const processApiImagesResponse = (response: any): ImageItem[] => {
    if (!response) return [];
    
    let apiImages: any[] = [];
    
    // Handle different response formats
    if (Array.isArray(response)) {
      apiImages = response;
    } else if (response.data && Array.isArray(response.data)) {
      apiImages = response.data;
    } else if (response.data && typeof response.data === 'object') {
      if (Array.isArray(response.data)) {
        apiImages = response.data;
      } else {
        // Extract objects with imageUrl property
        const entries = Object.values(response.data).filter(
          (entry: any) => entry && typeof entry === 'object' && 'imageUrl' in entry
        );
        apiImages = entries;
      }
    }
    
    // Handle single object response
    if (!Array.isArray(apiImages) && apiImages && typeof apiImages === 'object' && 'imageUrl' in apiImages) {
      apiImages = [apiImages];
    }
    
    // Map the API response to clean ImageItem objects
    const cleanedImages = (Array.isArray(apiImages) ? apiImages : [])
      .filter(img => img && (typeof img === 'object') && img.imageUrl)
      .map(img => ({
        id: img.id || 0,
        imageUrl: img.imageUrl || '',
        typeId: img.typeId || 1,
        createdBy: img.createdBy || '',
        createdDateTime: img.createdDateTime || '',
        pestControlScheduleId: img.pestControlScheduleId || 0,
        pestControlSchedule: img.pestControlSchedule
      } as ImageItem));
    
    return cleanedImages;
  };

  // Handle delete before image
  const handleDeleteBeforeImage = async (index: number) => {
    if (!schedule || !beforeImages) return;
    
    // Store all current images
    const allImages = [...beforeImages];
    const image = allImages[index];
    
    // Use custom confirm dialog
    const isConfirmed = await confirm('Are you sure you want to delete this image?');
    if (!isConfirmed) return;
    
    // Set flag to prevent GetSchedule calls
    isUpdatingImages.current = true;
    
    // Immediately remove the image from UI for responsive feel
    const newImageList = [...allImages];
    newImageList.splice(index, 1);
    
    // Update the beforeImages state directly
    setBeforeImages(newImageList);
    
    // Only call API if the image has an ID (meaning it's stored on server)
    if (typeof image !== 'string' && image.id) {
      try {
        setApiLoading(true);
        
        // Call the API to delete the image 
        await scheduleAPI.deleteImage(image.id);
        showSuccess('Image deleted successfully');
        
        // Optionally refresh the images from the server
        try {
          const imagesResponse = await scheduleAPI.getImages(schedule.id, 1);
          
          // Process the response using the improved parser
          const processedImages = processApiImagesResponse(imagesResponse);
          
          // Update the beforeImages state directly
          if (processedImages && processedImages.length >= 0) {
            setBeforeImages(processedImages);
          }
        } catch (refreshError) {
          console.error('Error refreshing images after deletion:', refreshError);
        }
      } catch (deleteError) {
        console.error('Error deleting image:', deleteError);
        showError('Failed to delete image. Please try again.');
        
        // Restore the original image list
        setBeforeImages(allImages);
      } finally {
        setApiLoading(false);
        
        // Clear flag after a short delay
        setTimeout(() => {
          isUpdatingImages.current = false;
        }, 1000);
      }
    } else {
      // Clear flag after a short delay if no API call was made
      setTimeout(() => {
        isUpdatingImages.current = false;
      }, 1000);
    }
  };

  // Handle delete after image
  const handleDeleteAfterImage = async (index: number) => {
    if (!schedule || !afterImages) return;
    
    // Store all current images
    const allImages = [...afterImages];
    const image = allImages[index];
    
    // Use custom confirm dialog
    const isConfirmed = await confirm('Are you sure you want to delete this image?');
    if (!isConfirmed) return;
    
    // Set flag to prevent GetSchedule calls
    isUpdatingImages.current = true;
    
    // Immediately remove the image from UI for responsive feel
    const newImageList = [...allImages];
    newImageList.splice(index, 1);
    
    // Update the afterImages state directly
    setAfterImages(newImageList);
    
    // Only call API if the image has an ID (meaning it's stored on server)
    if (typeof image !== 'string' && image.id) {
      try {
        setApiLoading(true);
        
        // Call the API to delete the image 
        await scheduleAPI.deleteImage(image.id);
        showSuccess('Image deleted successfully');
        
        // Optionally refresh the images from the server
        try {
          const imagesResponse = await scheduleAPI.getImages(schedule.id, 2);
          
          // Process the response using the improved parser
          const processedImages = processApiImagesResponse(imagesResponse);
          
          // Update the afterImages state directly
          if (processedImages && processedImages.length >= 0) {
            setAfterImages(processedImages);
          }
        } catch (refreshError) {
          console.error('Error refreshing images after deletion:', refreshError);
        }
      } catch (deleteError) {
        console.error('Error deleting image:', deleteError);
        showError('Failed to delete image. Please try again.');
        
        // Restore the original image list
        setAfterImages(allImages);
      } finally {
        setApiLoading(false);
        
        // Clear flag after a short delay
        setTimeout(() => {
          isUpdatingImages.current = false;
        }, 1000);
      }
    } else {
      // Clear flag after a short delay if no API call was made
      setTimeout(() => {
        isUpdatingImages.current = false;
      }, 1000);
    }
  };

  // Handle upload before images
  const handleBeforeImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !schedule) return;
    
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Set flag to prevent GetSchedule calls
    isUpdatingImages.current = true;
    
    // Store the existing images to preserve them
    const existingImages = [...beforeImages];
    
    // First, add blob URLs for immediate visual feedback
    const imageBlobs = files.map(file => URL.createObjectURL(file));
    
    // Update beforeImages state directly
    setBeforeImages([...existingImages, ...imageBlobs]);
    
    setApiLoading(true);
    
    try {
      // Loop through each file and upload individually
      for (const file of files) {
        await scheduleAPI.uploadImage(schedule.id, [file], 1);
      }
      
      // Show success message
      showSuccess('Before images uploaded successfully');
      
      // Fetch ONLY the images using getImages API
      try {
        const imagesResponse = await scheduleAPI.getImages(schedule.id, 1);
        
        // Use the common processor to extract images from the API response
        const processedImages = processApiImagesResponse(imagesResponse);
        
        if (processedImages.length > 0) {
          // Get non-blob existing images to preserve
          const nonBlobImages = existingImages.filter(
            img => typeof img !== 'string' || !img.startsWith('blob:')
          );
          
          // Make sure we don't have duplicates
          const existingIds = new Set(
            nonBlobImages
              .filter(img => typeof img !== 'string')
              .map((img: ImageItem) => img.id)
          );
          
          // Only add new images that aren't already in our list
          const newImages = processedImages.filter(img => !existingIds.has(img.id));
          
          // Update beforeImages state directly
          setBeforeImages([...nonBlobImages, ...newImages]);
        }
      } catch (refreshError) {
        console.error('Error fetching images after upload:', refreshError);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      showError('Failed to upload images. Please try again.');
      
      // Remove blob URLs on error
      const filteredImages = existingImages.filter(
        img => typeof img !== 'string' || !imageBlobs.includes(img as string)
      );
      
      setBeforeImages(filteredImages);
    } finally {
      setApiLoading(false);
      
      // Revoke blob URLs to prevent memory leaks
      imageBlobs.forEach(blob => URL.revokeObjectURL(blob as string));
      
      // Clear flag after a short delay
      setTimeout(() => {
        isUpdatingImages.current = false;
      }, 1000);
    }
  };

  // Handle upload after images
  const handleAfterImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !schedule) return;
    
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Set flag to prevent GetSchedule calls
    isUpdatingImages.current = true;
    
    // Store the existing images to preserve them
    const existingImages = [...afterImages];
    
    // First, add blob URLs for immediate visual feedback
    const imageBlobs = files.map(file => URL.createObjectURL(file));
    
    // Update afterImages state directly
    setAfterImages([...existingImages, ...imageBlobs]);
    
    setApiLoading(true);
    
    try {
      // Loop through each file and upload individually
      for (const file of files) {
        await scheduleAPI.uploadImage(schedule.id, [file], 2);
      }
      
      // Show success message
      showSuccess('After images uploaded successfully');
      
      // Fetch ONLY the images using getImages API
      try {
        const imagesResponse = await scheduleAPI.getImages(schedule.id, 2);
        
        // Use the common processor to extract images from the API response
        const processedImages = processApiImagesResponse(imagesResponse);
        
        if (processedImages.length > 0) {
          // Get non-blob existing images to preserve
          const nonBlobImages = existingImages.filter(
            img => typeof img !== 'string' || !img.startsWith('blob:')
          );
          
          // Make sure we don't have duplicates
          const existingIds = new Set(
            nonBlobImages
              .filter(img => typeof img !== 'string')
              .map((img: ImageItem) => img.id)
          );
          
          // Only add new images that aren't already in our list
          const newImages = processedImages.filter(img => !existingIds.has(img.id));
          
          // Update afterImages state directly
          setAfterImages([...nonBlobImages, ...newImages]);
        }
      } catch (refreshError) {
        console.error('Error fetching images after upload:', refreshError);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      showError('Failed to upload images. Please try again.');
      
      // Remove blob URLs on error
      const filteredImages = existingImages.filter(
        img => typeof img !== 'string' || !imageBlobs.includes(img as string)
      );
      
      setAfterImages(filteredImages);
    } finally {
      setApiLoading(false);
      
      // Revoke blob URLs to prevent memory leaks
      imageBlobs.forEach(blob => URL.revokeObjectURL(blob as string));
      
      // Clear flag after a short delay
      setTimeout(() => {
        isUpdatingImages.current = false;
      }, 1000);
    }
  };

  // Handle save notes
  const handleSaveNotes = async () => {
    if (!schedule) return;
    
    setApiLoading(true);
    
    try {
      // Call the API endpoint to save notes
      await scheduleAPI.saveNotes(schedule.id, { Remarks: notes });
      
      // Update local state
      setSchedule(prevSchedule => {
        if (!prevSchedule) return prevSchedule;
        return {
          ...prevSchedule,
          remarks: notes
        };
      });
      
      showSuccess('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      showError('Failed to save notes. Please try again.');
    } finally {
      setApiLoading(false);
    }
  };

  // Show completion form
  const showCompleteForm = () => {
    setShowCompletionForm(true);
  };

  // Handle recommendation selection
  const handleRecommendationChange = (recommendation: Recommendation, isChecked: boolean) => {
    if (isChecked) {
      setSelectedRecommendations(prev => [...prev, recommendation.id]);
    } else {
      setSelectedRecommendations(prev => prev.filter(recId => recId !== recommendation.id));
      
      // Clear any associated fields when unselecting a recommendation
      if (recommendation.requiresDate) {
        setFollowUpDate('');
      } else if (recommendation.requiresTextField) {
        setOtherFieldText('');
      }
    }
  };

  // Add helper functions to check schedule status
  const isSchedulePending = (schedule: ScheduleItem | null): boolean => {
    if (!schedule) return false;
    return schedule.workFlowStatusId === 0 || (!schedule.workFlowStatusId && !schedule.isCompleted);
  };

  const isScheduleInProgress = (schedule: ScheduleItem | null): boolean => {
    if (!schedule) return false;
    return schedule.workFlowStatusId === 1;
  };

  const isScheduleCompleted = (schedule: ScheduleItem | null): boolean => {
    if (!schedule) return false;
    return schedule.workFlowStatusId === 2 || schedule.isCompleted;
  };

  // Handle complete button
  const handleComplete = () => {
    // Check if schedule exists and is in progress (workFlowStatusId === 1)
    if (schedule && isScheduleInProgress(schedule)) {
      showCompleteForm();
    } else if (schedule && isSchedulePending(schedule)) {
      showError('Schedule must be in progress before it can be marked as completed. Please check in first.');
    }
  };

  // Handle submit completion form
  const handleSubmitCompletionForm = async () => {
    if (!schedule || !schedule.id) return;
    
    // Validation checks
    if (treatedPests.length === 0) {
      toast.error("Please select at least one pest type");
      return;
    }
    
    // Check if "Others" is selected but no specification is provided
    const othersSelected = pestTypes.some(pest => 
      pest.name === 'Others' && treatedPests.includes(pest.id)
    );
    
    if (othersSelected && !otherPestSpecification.trim()) {
      toast.error("Please specify the other pest type");
      return;
    }
    
    // Show service report confirmation instead of proceeding directly
    setShowServiceReportConfirm(true);
  };
  
  // Function to handle service report confirmation response
  const handleServiceReportConfirm = (confirm: boolean) => {
    setShowServiceReportConfirm(false);
    
    if (confirm) {
      // User wants to issue a service report
      setIsRequiredServiceReport(true);
      
      // Set contact number from schedule data if available
      if (schedule && schedule.contactNo) {
        setContactNo(schedule.contactNo);
      }
      
      // Show contact confirmation modal
      setShowContactConfirm(true);
    } else {
      // User doesn't want a service report, proceed with completion
      completeTask(false, "");
    }
  };
  
  // Function to handle contact confirmation
  const handleContactConfirm = (confirm: boolean) => {
    setShowContactConfirm(false);
    
    if (confirm) {
      // Proceed with the provided contact number
      completeTask(true, contactNo);
    } else {
      // User cancelled, go back to service report confirmation
      setShowServiceReportConfirm(true);
    }
  };
  
  // Function to actually complete the task
  const completeTask = async (isServiceReportRequired: boolean, contactNumber: string) => {
    if (!schedule || !schedule.id) return;
    
    setApiLoading(true);
    
    try {
      // Map the selected recommendations to the backend format
      const formattedRecommendations = selectedRecommendations.map(recId => {
        const recommendation = recommendations.find(r => r.id === recId);
        let additionalInfo = '';
        if (recommendation?.requiresDate && followUpDate) {
          additionalInfo = followUpDate;
        } else if (recommendation?.requiresTextField && otherFieldText) {
          additionalInfo = otherFieldText;
        }

        return {
          Id: recId,
          AdditionalInformation: additionalInfo
        };
      });

      // Format treated pests as an array of objects with Id and AdditionalInformation
      const formattedTreatedPests = treatedPests.map(pestId => {
        // Find the pest type from the pestTypes array to get the name
        const pestType = pestTypes.find(p => p.id === pestId);
        return {
          Id: pestId,
          AdditionalInformation: pestType?.name === 'Others' ? otherPestSpecification : ''
        };
      });

      // Call the API to mark schedule as completed
      await scheduleAPI.markAsCompleted(schedule.id, {
        OtherRemarks: otherRemarks,
        InfestationLevel: infestationLevel === 'low' ? 1 : infestationLevel === 'medium' ? 2 : infestationLevel === 'high' ? 3 : 0,
        PostTreatmentActivity: postTreatmentActivity === 'reduced' ? 1 : postTreatmentActivity === 'no_change' ? 2 : postTreatmentActivity === 'increased' ? 3 : 0,
        AffectedArea: affectedAreas,
        SiteManagerCompletedBy: 'N/A', // Default value
        Recommendation: formattedRecommendations,
        // Use the formatted array of treated pests
        TreatedPests: formattedTreatedPests,
        // Use the array structure for PestInfestationLevels
        PestInfestationLevels: pestInfestationLevels,
        // Add service report parameters
        IsRequiredServiceReport: isServiceReportRequired,
        ContactNo: contactNumber
      });

      // Update local state
      setSchedule(updateScheduleState(schedule));

      // Show success toast
      showSuccess('Schedule marked as completed successfully!');
      
      // Clear form data and hide form
      clearCompletionForm();
      setShowCompletionForm(false);
    } catch (error) {
      console.error('Error marking as completed:', error);
      // Provide more detailed error message if available
      if (error.response?.data?.message) {
        showError(`Failed to complete: ${error.response.data.message}`);
      } else {
        showError('Failed to mark as completed. Please ensure all required fields are filled and try again.');
      }
    } finally {
      setApiLoading(false);
    }
  };

  // Helper function to get a pest's infestation level data
  const getPestInfestationLevel = (pestId: number): { Level: 'none' | 'low' | 'medium' | 'high', Area: string } => {
    const found = pestInfestationLevels.find(item => item.Id === pestId);
    return found || { Level: 'none', Area: '' };
  };

  // Clear completion form state
  const clearCompletionForm = () => {
    setShowCompletionForm(false);
    setOtherRemarks('');
    setInfestationLevel('');
    setAffectedAreas('');
    setPostTreatmentActivity('');
    setSelectedRecommendations([]);
    setOtherFieldText('');
    setFollowUpDate('');
    setTreatedPests([]);
    setOtherPestSpecification('');
    setPestInfestationLevels([]);
  };

  // Add a function to get the workflow status text and color class based on workFlowStatusId
  const getWorkflowStatus = (schedule: ScheduleItem | null): { text: string; colorClass: string } => {
    if (!schedule) return { text: 'Unknown', colorClass: 'text-gray-500' };
    
    // Default to using workFlowStatusId if available
    if (typeof schedule.workFlowStatusId === 'number') {
      switch (schedule.workFlowStatusId) {
        case 0:
          return { text: 'Pending', colorClass: 'text-blue-500' };
        case 1:
          return { text: 'In Progress', colorClass: 'text-orange-500' };
        case 2:
          return { text: 'Completed', colorClass: 'text-green-500' };
        default:
          return { text: 'Unknown', colorClass: 'text-gray-500' };
      }
    }
    
    // Fall back to isCompleted for older data
    return schedule.isCompleted 
      ? { text: 'Completed', colorClass: 'text-green-500' }
      : { text: 'Pending', colorClass: 'text-blue-500' };
  };

  // Update the handlePestTypeChange function to manage the array of pest type IDs
  // This array will be used internally, and when submitting we'll format the data
  // to match the API requirements
  const handlePestTypeChange = (pestId: number, pestName: string) => {
    if (treatedPests.includes(pestId)) {
      setTreatedPests(treatedPests.filter(id => id !== pestId));
      // Clear other pest specification when Others is unchecked
      if (pestName === 'Others') {
        setOtherPestSpecification('');
      }
    } else {
      setTreatedPests([...treatedPests, pestId]);
    }
  };

  // Add handlePestInfestationLevelChange function
  const handlePestInfestationLevelChange = (
    pestId: number,
    level: 'none' | 'low' | 'medium' | 'high',
    area?: string,
    additionalInfo?: string
  ) => {
    setPestInfestationLevels(prev => {
      // Find if this pest type already exists in the array
      const existingIndex = prev.findIndex(item => item.Id === pestId);
      
      if (existingIndex !== -1) {
        // Update existing item
        const updatedLevels = [...prev];
        updatedLevels[existingIndex] = {
          ...updatedLevels[existingIndex],
          Level: level,
          Area: level === 'high' ? area || '' : '',
          AdditionalInformation: additionalInfo || ''
        };
        return updatedLevels;
      } else {
        // Add new item
        return [
          ...prev,
          {
            Id: pestId,
            Level: level,
            Area: level === 'high' ? area || '' : '',
            AdditionalInformation: additionalInfo || ''
          }
        ];
      }
    });
  };

  return (
    <div className="container-app py-6">
      {/* Show loading overlay for API operations */}
      {apiLoading && <LoadingOverlay />}
      
      <div className="md:flex items-center mb-6 hidden">
        <button 
          onClick={() => navigate(-1)}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 className="text-2xl font-bold">{schedule.projectName}</h1>
      </div>
      
      <div className="md:hidden mb-6">
        <h1 className="text-xl font-bold">{schedule.projectName}</h1>
      </div>

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`
          inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
          ${getWorkflowStatus(schedule).colorClass === 'text-green-500' ? 'bg-green-100' : 
            getWorkflowStatus(schedule).colorClass === 'text-orange-500' ? 'bg-orange-100' : 'bg-blue-100'}
        `}>
          {getWorkflowStatus(schedule).text}
        </span>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="flex border-b">
          <button
            className={`flex-1 py-3 font-medium text-sm focus:outline-none transition-all ${
              activeTab === 'details'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700'
            } ${activeIndicator === 'details' ? 'animate-tab-pulse bg-primary-light bg-opacity-10' : ''}`}
            onClick={() => handleTabChange('details')}
          >
            Details
          </button>
          <button
            className={`flex-1 py-3 font-medium text-sm focus:outline-none transition-all ${
              activeTab === 'before'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700'
            } ${activeIndicator === 'before' ? 'animate-tab-pulse bg-primary-light bg-opacity-10' : ''}`}
            onClick={() => handleTabChange('before')}
          >
            Before
          </button>
          <button
            className={`flex-1 py-3 font-medium text-sm focus:outline-none transition-all ${
              activeTab === 'after'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700'
            } ${activeIndicator === 'after' ? 'animate-tab-pulse bg-primary-light bg-opacity-10' : ''}`}
            onClick={() => handleTabChange('after')}
          >
            After
          </button>
          <button
            className={`flex-1 py-3 font-medium text-sm focus:outline-none transition-all ${
              activeTab === 'pesticides'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => handleTabChange('pesticides')}
          >
            Pesticides
          </button>
        </div>

        {/* Tab Content with Swipe Detection */}
        <div 
          className={`p-4 ${getAnimationClass()}`}
        >
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div>
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Location Details</h3>
                  <span className={`py-1 px-3 rounded-full text-sm 
                    ${getWorkflowStatus(schedule).colorClass === 'text-green-500' ? 'bg-green-100 text-green-800' : 
                      getWorkflowStatus(schedule).colorClass === 'text-orange-500' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}
                  `}>
                    {getWorkflowStatus(schedule).text}
                  </span>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <div className="w-24 text-gray-500 shrink-0">Date:</div>
                    <div>{schedule.date}</div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-24 text-gray-500 shrink-0">Time:</div>
                    <div>{schedule.time}</div>
                  </div>
                  {schedule.workFlowStatusId === 2 && (
                    <>
                      <div className="flex items-start">
                        <div className="w-24 text-gray-500 shrink-0">Start Time:</div>
                        <div>
                          {user?.position === 4 && isEditingManagerTimes ? (
                            <button
                              onClick={() => openPickerFor('start')}
                              className="px-3 py-2 border border-gray-300 rounded bg-white"
                            >
                              {managerStartInput ? format(new Date(managerStartInput), 'dd MMM yyyy, h:mm a') : 'Select start time'}
                            </button>
                          ) : (
                            schedule.managerStartedDate
                              ? format(new Date(schedule.managerStartedDate), 'dd MMM yyyy, h:mm a')
                              : '-'
                          )}
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-24 text-gray-500 shrink-0">End Time:</div>
                        <div>
                          {user?.position === 4 && isEditingManagerTimes ? (
                            <button
                              onClick={() => openPickerFor('end')}
                              className="px-3 py-2 border border-gray-300 rounded bg-white"
                            >
                              {managerEndInput ? format(new Date(managerEndInput), 'dd MMM yyyy, h:mm a') : 'Select end time'}
                            </button>
                          ) : (
                            schedule.managerCompletedDate
                              ? format(new Date(schedule.managerCompletedDate), 'dd MMM yyyy, h:mm a')
                              : '-'
                          )}
                        </div>
                      </div>
                      {user?.position === 4 && (
                        <div className="flex items-center gap-2 mt-2">
                          {isEditingManagerTimes ? (
                            <>
                              <button
                                onClick={handleSaveManagerTimes}
                                disabled={savingManagerTimes}
                                className="btn btn-primary"
                              >
                                {savingManagerTimes ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => { initManagerInputsFromSchedule(); setIsEditingManagerTimes(false); }}
                                className="btn bg-gray-100 text-gray-800 hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setIsEditingManagerTimes(true)}
                              className="btn bg-gray-100 text-gray-800 hover:bg-gray-200"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {schedule.customerName && (
                    <div className="flex items-start">
                      <div className="w-24 text-gray-500 shrink-0">Customer:</div>
                      <div className="font-medium flex items-center">
                        {schedule.customerName}
                        <Link
                          to={`/schedule-history/${id}`}
                          className="ml-2 text-primary font-medium inline-flex items-center hover:underline"
                          title="View History"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="ml-1 text-xs">History</span>
                        </Link>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start">
                    <div className="w-24 text-gray-500 shrink-0">
                      {schedule.address ? 'Address:' : schedule.remarks ? 'Remarks:' : 'Address:'}
                    </div>
                    {schedule.address ? (
                      <div 
                        className="whitespace-pre-line cursor-pointer text-primary hover:underline"
                        onClick={() => setShowMapPicker(true)}
                      >
                        {schedule.address.replace(/,/g, ',\n')}
                        <span className="inline-flex items-center ml-1 text-xs">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                          </svg>
                          <span className="ml-1">Navigate</span>
                        </span>
                      </div>
                    ) : schedule.remarks ? (
                      <div 
                        className="flex-1 schedule-remarks"
                        dangerouslySetInnerHTML={{ __html: schedule.remarks }}
                      />
                    ) : (
                      <div className="text-gray-400">No address or remarks</div>
                    )}
                  </div>
                  <div className="flex items-start">
                    <div className="w-24 text-gray-500 shrink-0">PIC:</div>
                    <div>{schedule.pic}</div>
                  </div>
                  {schedule.contactNo && (
                    <div className="flex items-start">
                      <div className="w-24 text-gray-500 shrink-0">Contact No:</div>
                      <div>
                        <a 
                          href={`https://wa.me/${formatWhatsAppNumber(schedule.contactNo)}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-600 hover:underline flex items-center"
                        >
                          <svg 
                            className="w-4 h-4 mr-1 text-green-600" 
                            fill="currentColor" 
                            viewBox="0 0 24 24" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          <span className="text-green-600">{schedule.contactNo}</span>
                        </a>
                      </div>
                    </div>
                  )}
                  {schedule.franchiseeBranchNo && (
                    <div className="flex items-start">
                      <div className="w-24 text-gray-500 shrink-0">Branch:</div>
                      <div>{schedule.franchiseeBranchNo}</div>
                    </div>
                  )}
                  {schedule.franchiseeId > 0 && (
                    <div className="flex items-start">
                      <div className="w-24 text-gray-500 shrink-0">Franchisee:</div>
                      <div>{schedule.franchiseeId}</div>
                    </div>
                  )}
                  {/* Only show remarks section if address exists (remarks already shown in address place if address is empty) */}
                  {schedule.remarks && schedule.address && (
                    <div className="mt-4">
                      <div className="text-gray-500 mb-1">Remarks:</div>
                      <div 
                        className="bg-gray-50 p-3 rounded schedule-remarks"
                        dangerouslySetInnerHTML={{ __html: schedule.remarks }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Completion Data (if task is completed) */}
              {schedule.workFlowStatusId === 2 && schedule.completionData && (
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Completion Details</h3>
                  
                  {/* Recommendations */}
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Recommendations:</h4>
                    <ul className="space-y-2 pl-5 list-disc">
                      {/* Display old format recommendations if available */}
                      {schedule.completionData.followUpRequired && (
                        <li>
                          Follow-up Visit Required
                          {schedule.completionData.followUpDate && (
                            <span className="font-medium"> (Date: {schedule.completionData.followUpDate})</span>
                          )}
                        </li>
                      )}
                      
                      {schedule.completionData.maintainCleanliness && (
                        <li>Maintain Cleanliness</li>
                      )}
                      
                      {schedule.completionData.sealCracks && (
                        <li>Seal Cracks & Entry Points</li>
                      )}
                      
                      {schedule.completionData.otherRecommendation && (
                        <li>Other: {schedule.completionData.otherRecommendation}</li>
                      )}
                      
                      {/* Display new format recommendations from pestControlScheduleRecommendations */}
                      {schedule.pestControlScheduleRecommendations?.map((rec, index) => {
                        // Find the recommendation name from the recommendations array
                        const recommendation = recommendations.find(r => r.id === rec.recommendationId);
                        const recommendationName = recommendation ? recommendation.name : `Recommendation ${rec.recommendationId}`;
                        
                        return (
                          <li key={`new-rec-${index}`}>
                            {recommendationName}
                            {rec.addtionalInfromation && rec.addtionalInfromation.trim() !== '' && (
                              <span> ({rec.addtionalInfromation})</span>
                            )}
                          </li>
                        );
                      })}
                      
                      {/* Only show "No recommendations provided" if both old and new formats have no recommendations */}
                      {!schedule.completionData.followUpRequired && 
                       !schedule.completionData.maintainCleanliness && 
                       !schedule.completionData.sealCracks && 
                       !schedule.completionData.otherRecommendation &&
                       (!schedule.pestControlScheduleRecommendations || schedule.pestControlScheduleRecommendations.length === 0) && (
                        <li className="text-gray-500">No recommendations provided</li>
                      )}
                    </ul>
                  </div>
                  
                  
                  {/* Observations */}
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Observations:</h4>
                    <div className="space-y-2">
                      {schedule.completionData.infestationLevel && (
                        <div>
                          <span className="text-gray-600">Infestation Level:</span>{' '}
                          <span className="font-medium capitalize">{schedule.completionData.infestationLevel}</span>
                        </div>
                      )}
                      
                      {schedule.completionData.affectedAreas && (
                        <div>
                          <span className="text-gray-600">Affected Areas:</span>{' '}
                          <span className="font-medium">{schedule.completionData.affectedAreas}</span>
                        </div>
                      )}
                      
                      {schedule.completionData.postTreatmentActivity && (
                        <div>
                          <span className="text-gray-600">Post-Treatment Activity:</span>{' '}
                          <span className="font-medium">
                            {schedule.completionData.postTreatmentActivity === 'no_change' 
                              ? 'No Change' 
                              : schedule.completionData.postTreatmentActivity.charAt(0).toUpperCase() + 
                                schedule.completionData.postTreatmentActivity.slice(1)}
                          </span>
                        </div>
                      )}
                      
                      {/* Display Treated Pests */}
                      {schedule.pestControlSchedulePestType && schedule.pestControlSchedulePestType.length > 0 && (
                        <div>
                          <span className="text-gray-600">Treated Pests:</span>{' '}
                          <span className="font-medium">
                            {(() => {
                              console.log('Rendering Treated Pests:', schedule.pestControlSchedulePestType);
                              console.log('Available Pest Types:', pestTypes);
                              
                              return schedule.pestControlSchedulePestType.map((pest, index) => {
                                // Find the pest type name from the pestTypes array
                                const pestType = pestTypes.find(type => type.id === pest.pestTypeId);
                                console.log(`Mapping pest ${index}:`, pest, 'Found pest type:', pestType);
                                
                                const pestName = pestType ? pestType.name : `Pest Type ${pest.pestTypeId}`;
                                
                                // For "Others" pest type, include the otherInformation
                                if (pestName === 'Others' && pest.otherInformation) {
                                  return `${pestName} (${pest.otherInformation})${index < (schedule.pestControlSchedulePestType?.length || 0) - 1 ? ', ' : ''}`;
                                }
                                
                                return `${pestName}${index < (schedule.pestControlSchedulePestType?.length || 0) - 1 ? ', ' : ''}`;
                              });
                            })()}
                          </span>
                        </div>
                      )}
                      
                      {/* Display Pest Infestation Levels */}
                      {schedule.pestControlScheduleInfestation && schedule.pestControlScheduleInfestation.length > 0 && (
                        <div className="mt-2">
                          <span className="text-gray-600">Pest Infestation Levels:</span>
                          <ul className="pl-5 mt-1 list-disc">
                            {schedule.pestControlScheduleInfestation.map((infestation, index) => {
                              // Find the infestation type name from the infestationTypes array
                              const infestationType = infestationTypes.find(type => type.id === infestation.infestationId);
                              const infestationName = infestationType ? infestationType.name : `Infestation Type ${infestation.infestationId}`;
                              
                              return (
                                <li key={index}>
                                  <span className="font-medium">{infestationName}:</span>{' '}
                                  <span className="capitalize">{infestation.infestationLevel}</span>
                                  {infestation.area && infestation.area.trim() !== '' && (
                                    <span> (Area: {infestation.area})</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  
                  {/* Other Remarks */}
                  {schedule.completionData.otherRemarks && (
                    <div>
                      <h4 className="font-medium mb-2">Other Remarks:</h4>
                      <p className="bg-gray-50 p-3 rounded">{schedule.completionData.otherRemarks}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">Notes</h3>
                <div className="mb-4">
                  <textarea
                    className="w-full border border-gray-300 rounded-md p-3 h-32 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter notes about this cleaning task..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  ></textarea>
                </div>
                <div className="flex justify-between">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveNotes}
                      className="btn bg-gray-100 text-gray-800 hover:bg-gray-200"
                    >
                      Save Notes
                    </button>
                  </div>
                  
                  {!isScheduleCompleted(schedule) && (
                    <button
                      onClick={handleComplete}
                      className={`btn ${isScheduleInProgress(schedule) ? 'btn-primary' : 'btn-disabled'}`}
                      disabled={!isScheduleInProgress(schedule)}
                    >
                      Mark as Completed
                    </button>
                  )}
                </div>
              </div>

              {/* Print Service Report - Only show if task is completed (workFlowStatusId = 2) */}
              {schedule && schedule.workFlowStatusId === 2 && (
                <div className="mt-6 border-t pt-6">
                  {/* Show notification only if signature is missing */}
                  {!schedule.signature && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">Signature Required</h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>This task is completed but still pending customer signature. The service report will include the signature after the customer signs it.</p>
                            
                            {schedule.lastSignatureRequestTime && (
                              <p className="mt-1">Last requested: {format(new Date(schedule.lastSignatureRequestTime), 'MMM dd, yyyy h:mm a')}</p>
                            )}
                            
                            <button
                              onClick={async () => {
                                if (isReminding) return;
                                
                                try {
                                  setIsReminding(true);
                                  await scheduleAPI.sendSignatureReminder(schedule.id);
                                  toast.success('Signature reminder sent successfully!');
                                } catch (error) {
                                  console.error('Error sending signature reminder:', error);
                                  toast.error('Failed to send reminder. Please try again.');
                                } finally {
                                  setIsReminding(false);
                                }
                              }}
                              disabled={isReminding}
                              className={`mt-3 px-3 py-1.5 rounded text-sm font-medium ${
                                isReminding
                                  ? 'bg-gray-200 text-gray-500'
                                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                }`}
                            >
                              {isReminding ? 'Sending...' : 'Send Reminder'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Replace the Print Service Report button with two buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={async () => {
                        if (isPrintingReport) return;
                        
                        try {
                          setIsPrintingReport(true);

                          const response = await scheduleAPI.printServiceReport(schedule.id);
                          
                          // Create a blob URL
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const blobUrl = window.URL.createObjectURL(blob);
                          
                          // Open the PDF in a new tab
                          window.open(blobUrl, '_blank');
                          
                          // Clean up the blob URL after a delay (give browser time to open it)
                          setTimeout(() => {
                            window.URL.revokeObjectURL(blobUrl);
                          }, 1000);
                        } catch (error) {
                          console.error('Error printing service report:', error);
                          toast.error('Failed to print service report. Please try again.');
                        } finally {
                          setIsPrintingReport(false);
                        }
                      }}
                      disabled={isPrintingReport}
                      className="flex-1 bg-primary text-white py-3 px-4 rounded-md flex items-center justify-center hover:bg-primary-dark transition-colors disabled:bg-gray-400"
                    >
                      {isPrintingReport ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Preview Report
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={async () => {
                        if (isPrintingReport) return;
                        
                        try {
                          setIsPrintingReport(true);
                          
                          const response = await scheduleAPI.printServiceReport(schedule.id);
                          
                          // Create a blob from the response data
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          
                          // Generate filename based on schedule details
                          const fileName = `ServiceReport_${schedule.projectName ? schedule.projectName.replace(/\s+/g, '_') : schedule.id}.pdf`;
                          
                          // Check if Web Share API is supported
                          if (navigator.share && navigator.canShare) {
                            const file = new File([blob], fileName, { type: 'application/pdf' });
                            
                            // Check if we can share files
                            if (navigator.canShare({ files: [file] })) {
                              await navigator.share({
                                files: [file],
                                title: 'Service Report',
                                text: `Service Report for ${schedule.projectName || 'Job'} on ${schedule.date}`
                              });
                              toast.success('Service report shared successfully');
                            } else {
                              // Fallback for browsers that support share but not file sharing
                              const blobUrl = window.URL.createObjectURL(blob);
                              window.open(blobUrl, '_blank');
                              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
                            }
                          } else {
                            // Fallback for browsers without Web Share API
                            const blobUrl = window.URL.createObjectURL(blob);
                            window.open(blobUrl, '_blank');
                            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
                          }
                        } catch (error) {
                          console.error('Error sharing service report:', error);
                          toast.error('Failed to share service report. Please try again.');
                        } finally {
                          setIsPrintingReport(false);
                        }
                      }}
                      disabled={isPrintingReport}
                      className="flex-1 bg-green-600 text-white py-3 px-4 rounded-md flex items-center justify-center hover:bg-green-700 transition-colors disabled:bg-gray-400"
                    >
                      {isPrintingReport ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Share Report
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Before Images Tab */}
          {activeTab === 'before' && (
            <div>
              <p className="text-gray-600 mb-4">
                Take photos before starting the cleaning work.
              </p>

              <div className="mb-4">
                <label className="block w-full py-2 px-3 border border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBeforeImageUpload}
                    multiple
                  />
                  <span className="text-primary flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Take Photos or Upload Multiple Images
                  </span>
                </label>
              </div>

              {isLoadingImages ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading images...</p>
                </div>
              ) : beforeImages && beforeImages.length > 0 ? (
                <div>
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium">Before Images ({beforeImages.length})</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {beforeImages.map((image, index) => {
                      // Debug log each image we're trying to render
                      const imageUrl = typeof image === 'string' 
                        ? image 
                        : (image && 'imageUrl' in image ? image.imageUrl : 'invalid-image');
                      console.log(`Rendering before image ${index}:`, imageUrl);
                      
                      return (
                      <ZoomableImage 
                        key={index} 
                        src={image} 
                        alt={`Before cleaning ${index + 1}`} 
                        index={index}
                          onDelete={handleDeleteBeforeImage}
                      />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-md">
                  <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-4 text-gray-600">No before images uploaded yet.</p>
                </div>
              )}
            </div>
          )}

          {/* After Images Tab */}
          {activeTab === 'after' && (
            <div>
              <p className="text-gray-600 mb-4">
                Take photos after completing the cleaning work.
              </p>

              <div className="mb-4">
                <label className="block w-full py-2 px-3 border border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAfterImageUpload}
                   
                    multiple
                  />
                  <span className="text-primary flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Take Photos or Upload Multiple Images
                  </span>
                </label>
              </div>

              {isLoadingImages ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading images...</p>
                </div>
              ) : afterImages && afterImages.length > 0 ? (
                <div>
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium">After Images ({afterImages.length})</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {afterImages.map((image, index) => (
                      <ZoomableImage 
                        key={index} 
                        src={image} 
                        alt={`After cleaning ${index + 1}`} 
                        index={index}
                        onDelete={handleDeleteAfterImage}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-md">
                  <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-4 text-gray-600">No after images uploaded yet.</p>
                </div>
              )}
            </div>
          )}
          
          {/* Add Pesticides Tab Content */}
          {activeTab === 'pesticides' && (
            <div className="py-2">
              <PesticideInputForm scheduleId={id} />
            </div>
          )}
        </div>
      </div>

      <div className="mb-20 md:mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/schedule" className="text-primary hover:underline">
            Back to All Schedules
          </Link>
          
          <Link to="/" className="text-primary hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Completion Form Modal */}
      {showCompletionForm && (
        <div className="fixed inset-0 flex flex-col z-50">
          <div className="bg-primary p-4 text-white flex items-center">
            <button 
              onClick={() => setShowCompletionForm(false)}
              className="mr-2 text-white"
              aria-label="Return"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2 className="text-white text-base font-bold">Complete Cleaning Task</h2>
              <p className="text-white text-xs opacity-90">Fill out the completion details below</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-white pb-20">
            <div className="p-4 space-y-5">
              {/* Recommendations */}
              <div>
                <h3 className="font-bold mb-3 text-gray-800 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  Recommendations:
                </h3>
                
                {recommendationLoading ? (
                  <div className="py-2 text-gray-500">Loading recommendations...</div>
                ) : recommendations.length > 0 ? (
                <div className="space-y-4">
                    {recommendations.map((recommendation) => (
                      <div key={recommendation.id} className="flex flex-col">
                        <div className="flex items-center">
                    <input 
                      type="checkbox" 
                            id={`recommendation-${recommendation.id}`} 
                            className="custom-checkbox"
                            checked={selectedRecommendations.includes(recommendation.id)}
                            onChange={(e) => handleRecommendationChange(recommendation, e.target.checked)}
                          />
                          <label htmlFor={`recommendation-${recommendation.id}`} className="ml-2 font-normal">
                            {recommendation.name}
                      </label>
                        </div>
                        
                        {/* Show date field if recommendation requires date and it's selected */}
                        {recommendation.requiresDate && selectedRecommendations.includes(recommendation.id) && (
                          <div className="ml-7 mt-2 relative">
                            <div 
                              className="border border-gray-300 rounded text-sm w-full flex items-center cursor-pointer bg-white"
                              onClick={() => setShowDatePicker(true)}
                            >
                              <input 
                                type="text" 
                                className="px-3 py-2 w-full cursor-pointer"
                                value={selectedDate ? formatDate(selectedDate) : ''}
                                placeholder="Select a date"
                                readOnly
                              />
                              <button className="px-2 text-gray-500">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Show text field if recommendation requires text field and it's selected */}
                        {recommendation.requiresTextField && selectedRecommendations.includes(recommendation.id) && (
                          <div className="ml-7 mt-2">
                      <input 
                        type="text" 
                              className="border border-gray-300 px-2 py-2 rounded text-sm w-full" 
                              value={otherFieldText}
                              onChange={(e) => setOtherFieldText(e.target.value)}
                              placeholder="Please specify..."
                      />
                    </div>
                        )}
                  </div>
                    ))}
                </div>
                ) : (
                  <div className="py-2 text-gray-500">No recommendations available</div>
                )}
              </div>

              {/* Observations & Recommendations */}
              <div>
                <h3 className="font-bold mb-3 text-gray-800 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                  </svg>
                  Observations & Recommendations
                </h3>
      
                
                <div className="mb-4">
                  <label htmlFor="affected" className="block mb-2 text-gray-800 flex items-center">
                    <svg className="w-4 h-4 mr-1 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                    </svg>
                    Affected Areas:
                  </label>
                  <input 
                    type="text" 
                    id="affected" 
                    className="border border-gray-300 w-full px-3 py-2 rounded text-sm bg-gray-50" 
                    value={affectedAreas}
                    onChange={(e) => setAffectedAreas(e.target.value)}
                    placeholder="Specify affected areas"
                  />
                </div>
                
                <div>
                  <p className="mb-2 text-gray-800 flex items-center">
                    <svg className="w-4 h-4 mr-1 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002-2V7a2 2 0 00-2-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd"></path>
                    </svg>
                    Post-Treatment Pest Activity:
                  </p>
                  <div className="flex flex-wrap gap-x-6">
                    <div className="flex items-center">
                      <input 
                        type="radio" 
                        id="reduced" 
                        name="postTreatmentActivity"
                        className="custom-radio"
                        checked={postTreatmentActivity === 'reduced'}
                        onChange={() => setPostTreatmentActivity('reduced')}
                      />
                      <label htmlFor="reduced" className="ml-1 font-normal">Reduced</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input 
                        type="radio" 
                        id="nochange" 
                        name="postTreatmentActivity"
                        className="custom-radio"
                        checked={postTreatmentActivity === 'no_change'}
                        onChange={() => setPostTreatmentActivity('no_change')}
                      />
                      <label htmlFor="nochange" className="ml-1 font-normal">No Change</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input 
                        type="radio" 
                        id="increased" 
                        name="postTreatmentActivity"
                        className="custom-radio"
                        checked={postTreatmentActivity === 'increased'}
                        onChange={() => setPostTreatmentActivity('increased')}
                      />
                      <label htmlFor="increased" className="ml-1 font-normal">Increased</label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Other Remarks */}
              <div>
                <h3 className="font-bold mb-2 text-gray-800 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd"></path>
                  </svg>
                  Other Remarks
                </h3>
                <textarea 
                  className="border border-gray-300 w-full px-3 py-2 rounded text-sm bg-gray-50 h-24" 
                  value={otherRemarks}
                  onChange={(e) => setOtherRemarks(e.target.value)}
                  placeholder="Enter any additional observations or remarks"
                ></textarea>
              </div>
              
              {/* Pest Control Section */}
              <div>
                <h3 className="text-lg font-medium mb-4 text-primary flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M6.625 2.655A9 9 0 0119 11a1 1 0 11-2 0 7 7 0 00-9.625-6.492 1 1 0 11-.75-1.853zM4.662 4.959A1 1 0 014.75 6.37 6.97 6.97 0 003 11a1 1 0 11-2 0 8.97 8.97 0 012.25-5.953 1 1 0 011.412-.088z" clipRule="evenodd"></path>
                    <path fillRule="evenodd" d="M5 11a5 5 0 1110 0 1 1 0 11-2 0 3 3 0 10-6 0c0 1.677-.345 3.276-.968 4.729a1 1 0 11-1.838-.789A9.964 9.964 0 005 11zm8.921 2.012a1 1 0 01.831 1.145 19.86 19.86 0 01-.545 2.436 1 1 0 11-1.92-.558c.207-.713.371-1.445.49-2.192a1 1 0 011.144-.83z" clipRule="evenodd"></path>
                  </svg>
                  Pest Control Information
                </h3>

                {/* Treated Pests Section */}
                <div className="mb-4 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-700 mb-3">
                    The above property has been treated for the control of the following pest(s): <span className="text-red-500">*</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {pestTypes.length > 0 ? (
                      pestTypes.map((pest) => (
                        <React.Fragment key={pest.id}>
                          <div className="flex items-center h-8">
                            <input
                              type="checkbox"
                              id={`pest-${pest.id}`}
                              checked={treatedPests.includes(pest.id)}
                              onChange={() => handlePestTypeChange(pest.id, pest.name)}
                              className="custom-checkbox"
                            />
                            <label 
                              htmlFor={`pest-${pest.id}`} 
                              className="ml-2 text-sm text-gray-700"
                            >
                              {pest.name}
                            </label>
                          </div>
                          {/* Add text input field for Others below the checkbox */}
                          {pest.name === 'Others' && treatedPests.includes(pest.id) && (
                            <div className="col-span-2 pl-6 -mt-1 mb-2">
                              <div className="flex items-center">
                                <input
                                  type="text"
                                  placeholder="Please specify *"
                                  value={otherPestSpecification}
                                  onChange={(e) => setOtherPestSpecification(e.target.value)}
                                  className="border rounded px-2 py-1 text-sm w-full"
                                  required
                                />
                                <span className="text-red-500 ml-1">*</span>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      // Fallback to hardcoded values if API fails
                      [
                        { id: 1, name: 'Subterranean termites' },
                        { id: 2, name: 'Cockroaches' },
                        { id: 3, name: 'Ants' },
                        { id: 4, name: 'Fleas / Ticks / Bedbugs' },
                        { id: 5, name: 'Mosquitoes' },
                        { id: 6, name: 'Rats' },
                        { id: 7, name: 'Flies' },
                        { id: 8, name: 'Others' }
                      ].map((pest) => (
                        <React.Fragment key={pest.id}>
                          <div className="flex items-center h-8">
                            <input
                              type="checkbox"
                              id={`pest-${pest.id}`}
                              checked={treatedPests.includes(pest.id)}
                              onChange={() => handlePestTypeChange(pest.id, pest.name)}
                              className="custom-checkbox"
                            />
                            <label 
                              htmlFor={`pest-${pest.id}`} 
                              className="ml-2 text-sm text-gray-700"
                            >
                              {pest.name}
                            </label>
                          </div>
                          {/* Add text input field for Others below the checkbox */}
                          {pest.name === 'Others' && treatedPests.includes(pest.id) && (
                            <div className="col-span-2 pl-6 -mt-1 mb-2">
                              <div className="flex items-center">
                                <input
                                  type="text"
                                  placeholder="Please specify *"
                                  value={otherPestSpecification}
                                  onChange={(e) => setOtherPestSpecification(e.target.value)}
                                  className="border rounded px-2 py-1 text-sm w-full"
                                  required
                                />
                                <span className="text-red-500 ml-1">*</span>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>

                {/* Infestation Levels Section */}
                <div className="border border-red-200 rounded-lg p-4 mb-4">
                  {/* Findings (Pest Problems) */}
                  <h3 className="text-md font-semibold mb-4 text-primary">Findings (Pest Problems):</h3>
                  
                  {/* Dynamically render infestation level sections for each pest type */}
                  {infestationTypes && infestationTypes.length > 0 ? (
                    infestationTypes.map(pest => {
                      // Debug logging with console.log outside of JSX
                      console.log('[Render] Rendering pest:', pest);
                      return (
                        <div key={`pest-infestation-${pest.id}`} className="mb-3">
                          <p className="text-sm font-medium mb-1">{pest.name} Infestation Level:</p>
                          <div className="flex items-center flex-wrap gap-2">
                            {['none', 'low', 'medium', 'high'].map(level => {
                              const pestData = getPestInfestationLevel(pest.id);
                              console.log(`[Render] Pest ${pest.id} (${pest.name}) level data:`, pestData);
                              return (
                                <label key={`${pest.id}-${level}`} className="flex items-center h-8">
                                  <input
                                    type="radio"
                                    name={`pest-${pest.id}-level`}
                                    checked={pestData.Level === level}
                                    onChange={() => handlePestInfestationLevelChange(
                                      pest.id, 
                                      level as 'none' | 'low' | 'medium' | 'high'
                                    )}
                                    className="custom-radio"
                                  />
                                  <span className="ml-1 text-sm text-gray-700 capitalize">{level}</span>
                                </label>
                              );
                            })}
                            {/* Show area input field when level is high */}
                            {getPestInfestationLevel(pest.id).Level === 'high' && (
                              <input
                                type="text"
                                placeholder="Specify area"
                                value={getPestInfestationLevel(pest.id).Area}
                                onChange={(e) => handlePestInfestationLevelChange(
                                  pest.id, 
                                  'high', 
                                  e.target.value
                                )}
                                className="input ml-2"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div>
                      {/* Empty div when there's no API data */}
                    </div>
                  )}

                  {/* Other fields like Other Remarks continue below */}
                  <div>
                    <h3 className="font-bold mb-2 text-gray-800 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd"></path>
                      </svg>
                      Other Remarks
                    </h3>
                    <textarea 
                      className="border border-gray-300 w-full px-3 py-2 rounded text-sm bg-gray-50 h-24" 
                      value={otherRemarks}
                      onChange={(e) => setOtherRemarks(e.target.value)}
                      placeholder="Enter any additional observations or remarks"
                    ></textarea>
                  </div>
                  
                  {/* Others checkbox with specification field */}
              
                  
                  {/* Submit Button */}
                  <div className="pt-6 pb-4">
                    <button
                      className="w-full py-3 bg-primary text-white rounded-md font-medium flex items-center justify-center"
                      onClick={handleSubmitCompletionForm}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                          </svg>
                          <span>Submit & Complete Task</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="fixed bottom-0 left-0 right-0 p-4 border-t border-gray-200 flex justify-between bg-white shadow-md z-50">
            <button 
              onClick={() => setShowCompletionForm(false)}
              className="px-6 py-3 text-gray-700 hover:text-gray-900 text-sm font-medium"
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmitCompletionForm}
              className="px-8 py-3 bg-primary text-white rounded-md hover:bg-primary-dark text-sm font-medium shadow-sm"
            >
              Complete Task
            </button>
          </div>
        </div>
      )}

      {/* Add Mobile Date Picker component at the end of the component */}
      <MobileDatePicker
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelectDate={handleDateSelect}
        selectedDate={selectedDate}
        initialDate={selectedDate || new Date()}
      />

      {/* Service Report Confirmation Modal */}
      {showServiceReportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Issue Service Report</h3>
            <p className="text-gray-600 mb-6">
              Would you like to issue a service report for this task?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => handleServiceReportConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              >
                No
              </button>
              <button
                onClick={() => handleServiceReportConfirm(true)}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Confirmation Modal */}
      {showContactConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Contact Number</h3>
            <p className="text-gray-600 mb-4">
              Please confirm the contact number for the service report:
            </p>
            <div className="flex space-x-2 mb-6">
              <input
                type="text"
                value={contactNo}
                onChange={(e) => setContactNo(e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded"
                placeholder="Enter contact number"
              />
              {contactNo && (
                <a 
                  href={`https://wa.me/${formatWhatsAppNumber(contactNo)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center p-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                  title="Open in WhatsApp"
                >
                  <svg 
                    className="w-5 h-5" 
                    fill="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => handleContactConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleContactConfirm(true)}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Picker Modal */}
      {showMapPicker && schedule && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={schedule.address}
        />
      )}

      <MobileDateTimePicker
        isOpen={showDateTimePicker}
        onClose={() => { setShowDateTimePicker(false); setActiveManagerField(null); }}
        onConfirm={handleConfirmDateTime}
        initialDateTime={activeManagerField === 'start' ? parseLocalInput(managerStartInput) : parseLocalInput(managerEndInput)}
      />
    </div>
  );
};

export default ScheduleDetail; 